import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import {
  Button,
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";

import { Cancel, DeleteForever, Download, MoreVert } from "@mui/icons-material";
import { DEFAULT_PAGE } from "./constants";
import { unzip } from "fflate";
import { useSearchParams } from "react-router-dom";
import {
  ApiEnabledPage,
  AuthHelper,
  CircularProgressWithLabel,
  GetOrderByDirection,
  GetOrderByIndex,
  GetPage,
  GetPageSize,
  RenderJobInfo,
  RenderJobStatus,
  RenderJobStatusToColor,
  RenderJobStatusToName,
  SetCurrentPage,
  SetOrderByDirection,
  SetOrderByIndex,
  SetPageSize,
  TimeSince } from "../../../../WebCommon/src";
import { SortableSearchableTable, TableColumnData, TopBar } from "../components";

const DEFAULT_PAGE_SIZE: number = 10;

const logout = (authHelper: AuthHelper, setIsLoggedIn: (state: boolean) => void) => {
  authHelper.Logout();
  setIsLoggedIn(false);

};

export const HomePage = (props: ApiEnabledPage) => {
  const { baseApiUrl, authHelper } = props;
  const [userJobs, setUserJobs] = useState<RenderJobInfo[]>([]);
  const [numJobs, setNumJobs] = useState<number>(-1);
  const [downloading, setDownloading] = useState<string>("");
  const [operationsMenuAnchor, setOperationsMenuAnchor] = useState<null | HTMLElement>(null);
  const [operationsMenuSelectedJob, setOperationsMenuSelectedJob] = useState<null | RenderJobInfo>(null);
  const [searchParams,] = useSearchParams();

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(authHelper.IsLoggedIn());
  const loginState = authHelper.GetLoginState();

  const navigate = useNavigate();
  const pageSizeOptions: number[] = [5, 10, 25, 100];
  const page = GetPage(numJobs, pageSizeOptions, DEFAULT_PAGE_SIZE, DEFAULT_PAGE, searchParams);
  const pageSize = GetPageSize(pageSizeOptions, DEFAULT_PAGE_SIZE, searchParams);

  const jobsTableConfig: TableColumnData<RenderJobInfo>[] = [{
    Index: 0,
    Sortable: true,
    DisplayName: "Name",
    PropertyName: "Name",
    Width: "1vw",
    ValueGetter(job) {
      return <Typography>{job.Name}</Typography>;
    },
  }, {
    Index: 1,
    Sortable: true,
    DisplayName: "Created",
    PropertyName: "Submitted",
    Width: "1vw",
    ValueGetter: (job) => <TimeSince Date={job.Submitted} Width="1vw" />
  }, {
    Index: 2,
    Sortable: true,
    DisplayName: "Status",
    PropertyName: "Status",
    Width: "1vw",
    ValueGetter(job) {
      return <Chip label={RenderJobStatusToName(job.Status)} color={RenderJobStatusToColor(job.Status)} />;
    },
  }, {
    Index: 3,
    Sortable: true,
    DisplayName: "Progress",
    PropertyName: "Completion",
    Width: "1vw",
    ValueGetter: (job) => <CircularProgressWithLabel color={job.Completion === 1.0 ? "success" : "primary"} variant="determinate" value={(job.Completion * 100)} />

  }, {
    Index: 4,
    Sortable: true,
    DisplayName: "More Information",
    PropertyName: "StatusMessage",
    Width: "1vw",
    ValueGetter: (job) => <Typography>{job.StatusMessage}</Typography>

  }, {
    Index: 5,
    Sortable: false,
    DisplayName: "Download",
    Width: "1vw",
    ValueGetter: (job) => {
      const content =
        <Button
          disabled={downloading === job.id || job.Status !== RenderJobStatus.Complete}
          onClick={() => OnDownloadResult(job)}
          variant="contained">
          {downloading === job.id
            ? <CircularProgress />
            : <Download />}
        </Button>;
      return content;

    }
  }, {
    Index: 6,
    Sortable: false,
    Width: "1vw",
    ValueGetter: (job) => <Button onClick={(evt) => onOperationsButtonClicked(evt, job)}><MoreVert /></Button>

  }];

  const orderByIndex = GetOrderByIndex(jobsTableConfig.length, 1, searchParams);
  const orderByDirection = GetOrderByDirection(searchParams, "desc");

  const operationsMenuOpened = Boolean(operationsMenuAnchor);

  const numSeconds: number = 5;
  const secondsToMili: number = 1000;

  const onClickCancelMenuItem = async () => {
    if (loginState) {

      const apiKey = loginState.apiKey;
      if (operationsMenuSelectedJob && apiKey) {
        await fetch(baseApiUrl + "CancelJob", {
          "method": "POST",
          "body": JSON.stringify({ JobId: operationsMenuSelectedJob.id }),
          headers: {
            'Authentication': apiKey,
          },
        });
      }
    }
    setOperationsMenuAnchor(null);

  };

  const onClickDeleteMenuItem = async () => {
    if (loginState) {

      const apiKey = loginState.apiKey;
      if (operationsMenuSelectedJob && apiKey) {

        await fetch(baseApiUrl + "DeleteJob", {
          "method": "POST",
          "body": JSON.stringify({ JobId: operationsMenuSelectedJob.id }),
          headers: {
            'Authentication': apiKey,
          },
        });

      }
    }

    setOperationsMenuAnchor(null);
  };

  useEffect(() => {
    if (loginState) {

      const queryParams = "status=Submitted&status=Assigned&status=Queued&status=InProgress&status=Complete&status=Crashed";

      fetch(baseApiUrl + `GetNumJobs?${queryParams} `, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authentication: loginState.apiKey
        }
      }).then(res => {

        if (res.status === 200) {
          res.json().then(json => {
            setNumJobs(+json.Count);

          });
        } else if (res.status === 401) {
          logout(authHelper, setIsLoggedIn);
        }
      });
    }

  }, [userJobs]);

  useEffect(() => {

    let anyInProgress: boolean = false;
    userJobs.map((job => {
      if (job.Status !== RenderJobStatus.Complete && job.Status !== RenderJobStatus.Crashed) {
        anyInProgress = true;
      }

    }));

    let interval: number | null = null;

    if (anyInProgress) {

      interval = setInterval(() => {
      }, numSeconds * secondsToMili);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };

  }, []);

  const OnDownloadResult = async (job: RenderJobInfo): Promise<void> => {
    if (loginState) {

      const apiKey = loginState.apiKey;
      if (apiKey) {

        setDownloading(job.id);
        const result = await fetch(baseApiUrl + "RequestDownloadUri", {
          method: "POST",
          headers: {
            'Authentication': apiKey,
          },
          body: JSON.stringify({ JobId: job.id }),
        });
        const uriObj = await result.json();

        const uri: string = uriObj.Uri;

        const storageResult = await fetch(uri);

        const blob = await storageResult.blob();
        const array = await blob.arrayBuffer();
        const u8Array = new Uint8Array(array);

        let numFiles: number = 0;

        unzip(u8Array, {
          filter: () => {
            numFiles += 1;
            return false;
          }
        }, () => {
          if (numFiles > 1) {
            const link = document.createElement("a");
            link.download = `${job.Name}.zip`;
            link.href = uri;
            link.target = "_blank";
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloading("");

          } else {
            let unzippedFilename: string = "";

            unzip(u8Array, {
              filter: (file) => {
                unzippedFilename = file.name;
                return true;
              }
            }, (error, data) => {
              if (!error) {

                const array = data[unzippedFilename];

                const link = document.createElement("a");
                const reader = new FileReader();
                // Create new Uint8Array from the buffer to ensure TypeScript compatibility
                const blob = new Blob([new Uint8Array(array)]);
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                  if (typeof reader.result === "string") {
                    link.setAttribute("href", reader.result);
                    const fileExt = unzippedFilename.split('.').pop();

                    link.setAttribute('download', `${job.Name}.${fileExt}`);
                    link.click();
                    setDownloading("");
                  }
                };

              }
            });
          }
        });
      }
    }
  };

  const fetchJobs = async () => {
    if (loginState) {

      setUserJobs([]);

      const fieldName = jobsTableConfig[orderByIndex].PropertyName;

      const queryParams = `page=${page}` +
        `&pageSize=${pageSize}&orderBy=${fieldName}&Direction=${orderByDirection.toUpperCase()}` +
        `&status=Submitted&status=Assigned&status=Queued&status=InProgress&status=Complete&status=Crashed`;

      const apiKey = loginState.apiKey;
      if (apiKey) {
        const res = await fetch(baseApiUrl + "GetJobs?" + queryParams, {
          method: "GET",
          headers: {
            'Accept': 'application/json',
            'Authentication': apiKey,
          },
        });

        if (res.status === 200) {
          const data = await res.json();
          setUserJobs(data);
        } else if (res.status === 401) {
          logout(authHelper, setIsLoggedIn);

        }
      }
    }
  };

  useEffect(() => {
    fetchJobs();

  }, [numJobs, page, pageSize, orderByIndex, orderByDirection]);

  const onOperationsButtonClicked = (event: React.MouseEvent<HTMLElement>, job: RenderJobInfo): void => {
    setOperationsMenuAnchor(event.currentTarget);
    setOperationsMenuSelectedJob(job);
  };

  if (!isLoggedIn) {
    return <Navigate to="/Login" />;
  }

  const onClickSort = async (Index: number) => {
    if (Index === orderByIndex) {
      SetOrderByDirection(searchParams, orderByDirection === "asc" ? "desc" : "asc", navigate);
      SetOrderByIndex(Index, jobsTableConfig.length, searchParams, navigate);

    } else {
      SetOrderByDirection(searchParams, orderByDirection, navigate);
      SetOrderByIndex(Index, jobsTableConfig.length, searchParams, navigate);

    }

  };

  return (
    <>
      <TopBar authHelper={authHelper} logout={() => {
        authHelper.Logout();
        setIsLoggedIn(false);
      }} />
      <Stack className="centeredBox">
        <Typography variant="h2">Render Jobs</Typography>

        <SortableSearchableTable
          className={`jobList jobList-max-dims jobList-prod-dims dottedBorder`}
          DataItems={userJobs}
          NumTotalItems={numJobs}
          PageSize={pageSize}
          PageSizeOptions={pageSizeOptions}
          SetPageSize={(val: number) => SetPageSize(val, pageSizeOptions, DEFAULT_PAGE_SIZE, searchParams, navigate)}
          Page={page}
          SetPage={(val: number) => SetCurrentPage(
            val,
            numJobs,
            searchParams,
            pageSizeOptions,
            DEFAULT_PAGE_SIZE,
            navigate)}
          OnClickSort={(val) => onClickSort(val)}
          OrderBy={{ Dir: orderByDirection, Index: orderByIndex }}
          TableColumnData={jobsTableConfig}
        />

        <Menu
          open={operationsMenuOpened}
          anchorEl={operationsMenuAnchor}
          onClose={() => setOperationsMenuAnchor(null)}>
          <MenuItem
            onClick={onClickDeleteMenuItem}
            disabled={operationsMenuSelectedJob?.Status !== RenderJobStatus.Complete}>

            <ListItemIcon>
              <DeleteForever />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">Delete</Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem
            onClick={onClickCancelMenuItem}
            disabled={
              operationsMenuSelectedJob?.Status === RenderJobStatus.Complete
              || operationsMenuSelectedJob?.Status === RenderJobStatus.Deleted
            }>
            <ListItemIcon>
              <Cancel />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">Cancel</Typography>
            </ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
    </>);
};
